import axios from 'axios';
import { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Wrench, X, User, MapPin, ImageIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';

function MiniProductionCalendar({ selected, onSelect, existingBons }) {
  const [month, setMonth] = useState(new Date());
  const DAYS = ['D', 'L', 'M', 'M', 'J', 'V', 'S'];

  const dates = useMemo(() => {
    const y = month.getFullYear(), m = month.getMonth();
    const first = new Date(y, m, 1);
    const last = new Date(y, m + 1, 0);
    const cells = [];
    for (let i = 0; i < first.getDay(); i++) cells.push(null);
    for (let d = 1; d <= last.getDate(); d++) cells.push(new Date(y, m, d));
    return cells;
  }, [month]);

  const busyDates = useMemo(() => {
    const set = new Set();
    (existingBons || []).forEach(b => (b.dates_chantier || []).forEach(d => set.add(d)));
    return set;
  }, [existingBons]);

  const toStr = (d) => d.toISOString().split('T')[0];

  return (
    <div className="border rounded-lg p-3 bg-white">
      <div className="flex items-center justify-between mb-2">
        <button onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1))} className="p-1 hover:bg-gray-100 rounded">
          <ChevronLeft className="w-4 h-4" />
        </button>
        <span className="text-sm font-medium">
          {month.toLocaleDateString('fr-CA', { month: 'long', year: 'numeric' })}
        </span>
        <button onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1))} className="p-1 hover:bg-gray-100 rounded">
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
      <div className="grid grid-cols-7 gap-0.5 text-center">
        {DAYS.map(d => <div key={d} className="text-[10px] text-gray-400 py-1">{d}</div>)}
        {dates.map((date, i) => {
          if (!date) return <div key={`empty-${i}`} />;
          const str = toStr(date);
          const isBusy = busyDates.has(str);
          const isSelected = selected === str;
          const isToday = toStr(new Date()) === str;
          return (
            <button
              key={str}
              onClick={() => onSelect(str)}
              className={`text-xs py-1.5 rounded transition-colors relative ${
                isSelected ? 'bg-orange-500 text-white font-bold' :
                isBusy ? 'bg-blue-50 text-blue-700 hover:bg-blue-100' :
                isToday ? 'bg-gray-100 font-medium' :
                'hover:bg-gray-50'
              }`}
            >
              {date.getDate()}
              {isBusy && !isSelected && <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-blue-500" />}
            </button>
          );
        })}
      </div>
      <div className="flex gap-3 mt-2 text-[10px] text-gray-500">
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-500" />Production</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-orange-500" />Service call</span>
      </div>
    </div>
  );
}

export function CreerServiceCallDialog({ isOpen, onClose, dossier, bon, onCreated }) {
  const [employees, setEmployees] = useState([]);
  const [existingBons, setExistingBons] = useState([]);
  const [form, setForm] = useState({
    description: '',
    notes: '',
    date_prevue: '',
    employes: [],
  });
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      axios.get(`${API_URL}/api/employees`)
        .then(r => r.data)
        .then(d => setEmployees(d.employees || []))
        .catch(() => {});
      axios.get(`${API_URL}/api/crm/bons-travail`)
        .then(r => r.data)
        .then(d => setExistingBons(d.bons_travail || []))
        .catch(() => {});
      // Pre-select employees from the bon
      const bonEmployes = [];
      if (bon?.chef_equipe_id) {
        bonEmployes.push({ id: bon.chef_equipe_id, nom: bon.chef_equipe_nom || '' });
      }
      (bon?.employes_assignes || []).forEach(e => {
        if (!bonEmployes.find(x => x.id === e.id)) {
          bonEmployes.push({ id: e.id, nom: e.nom || '' });
        }
      });
      setForm(f => ({ ...f, employes: bonEmployes }));
    }
  }, [isOpen, bon]);

  const toggleEmployee = (emp) => {
    setForm(f => {
      const exists = f.employes.find(e => e.id === emp.id);
      if (exists) {
        return { ...f, employes: f.employes.filter(e => e.id !== emp.id) };
      }
      return { ...f, employes: [...f.employes, { id: emp.id, nom: `${emp.prenom} ${emp.nom}` }] };
    });
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    const newPhotos = [];
    for (const file of files) {
      const reader = new FileReader();
      const dataUrl = await new Promise(resolve => {
        reader.onload = () => resolve(reader.result);
        reader.readAsDataURL(file);
      });
      newPhotos.push({ file, preview: dataUrl });
    }
    setPhotos(prev => [...prev, ...newPhotos]);
    setUploading(false);
  };

  const removePhoto = (idx) => setPhotos(p => p.filter((_, i) => i !== idx));

  const handleSubmit = async () => {
    if (!form.description.trim()) {
      toast.error('Veuillez entrer une description');
      return;
    }
    if (!form.date_prevue) {
      toast.error('Veuillez choisir une date prévue');
      return;
    }
    setSaving(true);
    let createdSC = null;
    let photosFailed = 0;
    const photosTotal = photos.filter(p => p.file).length;
    try {
      const payload = {
        dossier_id: dossier?.id || '',
        bon_travail_id: bon?.id || '',
        client_id: dossier?.client_id || '',
        client_nom: dossier?.client_nom || '',
        numero_dossier: dossier?.numero_dossier || '',
        nom_dossier: dossier?.nom_dossier || '',
        adresse_travaux: [dossier?.adresse_travaux, dossier?.ville_travaux].filter(Boolean).join(', '),
        description: form.description,
        notes: form.notes,
        employes: form.employes,
        date_prevue: form.date_prevue,
        date_service: form.date_prevue,
        chef_equipe_id: bon?.chef_equipe_id || '',
        chef_equipe_nom: bon?.chef_equipe_nom || '',
      };
      // ── PHASE 1 : créer le service call ──
      let res;
      try {
        res = await axios.post(`${API_URL}/api/crm/service-calls`, payload);
      } catch (err) {
        const detail = err?.response?.data?.detail || err?.message || 'Erreur réseau';
        toast.error(`Échec création du service call : ${detail}`);
        return;
      }
      if (res.status !== 200) {
        toast.error(`Échec création du service call (HTTP ${res.status})`);
        return;
      }
      createdSC = res.data;

      // ── PHASE 2 : upload photos (chaque photo en isolation, avec retry x2) ──
      // 🆕 2026-05-14 — Retry automatique 2x par photo en cas d'échec réseau
      //   pour éviter les pertes silencieuses observées en production.
      if (photosTotal > 0) {
        const { compressImageIfNeeded } = await import('@/lib/imageCompression');
        for (const photo of photos) {
          if (!photo.file) continue;
          let lastErr = null;
          let uploaded = false;
          for (let attempt = 1; attempt <= 3 && !uploaded; attempt++) {
            try {
              const compressed = await compressImageIfNeeded(photo.file);
              const fd = new FormData();
              fd.append('file', compressed);
              await axios.post(`${API_URL}/api/crm/service-calls/${createdSC.service_call_id}/photos`, fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
                timeout: 30000,
              });
              uploaded = true;
            } catch (errPhoto) {
              lastErr = errPhoto;
              // eslint-disable-next-line no-console
              console.warn(`Photo upload attempt ${attempt}/3 failed`, errPhoto?.response?.status, errPhoto?.message);
              if (attempt < 3) await new Promise(r => setTimeout(r, 800 * attempt));
            }
          }
          if (!uploaded) {
            photosFailed += 1;
            // eslint-disable-next-line no-console
            console.error('Photo upload definitively failed', lastErr?.response?.data || lastErr?.message);
          }
        }
      }

      // ── PHASE 3 : feedback différencié ──
      if (photosFailed === 0) {
        toast.success(photosTotal > 0
          ? `Service call créé avec ${photosTotal} photo${photosTotal > 1 ? 's' : ''} !`
          : 'Service call créé avec succès !');
      } else if (photosFailed < photosTotal) {
        toast.warning(`Service call créé, mais ${photosFailed}/${photosTotal} photo${photosFailed > 1 ? 's ont' : ' a'} échoué à l'upload. Vous pouvez les ré-ajouter depuis la fiche du service call.`);
      } else {
        toast.warning(`Service call créé, mais toutes les photos ont échoué à l'upload. Vous pouvez les ré-ajouter depuis la fiche du service call.`);
      }
      setForm({ description: '', notes: '', date_prevue: '', employes: [] });
      setPhotos([]);
      onClose();
      if (onCreated) onCreated();
    } catch (e) {
      // Filet de sécurité — ne devrait être atteint que pour un bug inattendu.
      const detail = e?.response?.data?.detail || e?.message || 'Erreur inconnue';
      if (createdSC) {
        toast.warning(`Service call créé mais un problème est survenu : ${detail}`);
        if (onCreated) onCreated();
      } else {
        toast.error(`Erreur lors de la création : ${detail}`);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wrench className="w-5 h-5 text-orange-600" />
            Créer un Service Call
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Infos client/dossier (read-only) */}
          <Card className="bg-slate-50">
            <CardContent className="py-3 space-y-1">
              <div className="flex items-center gap-2 text-sm">
                <User className="w-3.5 h-3.5 text-gray-400" />
                <span className="font-medium">{dossier?.client_nom}</span>
                <Badge variant="outline" className="text-[10px]">{dossier?.numero_dossier}</Badge>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <MapPin className="w-3.5 h-3.5 text-gray-400" />
                {dossier?.adresse_travaux}, {dossier?.ville_travaux}
              </div>
              <p className="text-sm text-gray-600">{dossier?.nom_dossier}</p>
            </CardContent>
          </Card>

          {/* Description */}
          <div>
            <label className="text-sm font-medium mb-1 block">Description du service call *</label>
            <textarea
              value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              className="w-full border rounded-lg p-3 text-sm min-h-[80px] resize-y"
              placeholder="Décrire le travail à effectuer..."
              data-testid="sc-description"
            />
          </div>

          {/* Notes */}
          <div>
            <label className="text-sm font-medium mb-1 block">Notes additionnelles</label>
            <textarea
              value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              className="w-full border rounded-lg p-3 text-sm min-h-[60px] resize-y"
              placeholder="Notes supplémentaires..."
              data-testid="sc-notes"
            />
          </div>

          {/* Photos */}
          <div>
            <label className="text-sm font-medium mb-1 block">Photos</label>
            <div className="flex flex-wrap gap-2 mb-2">
              {photos.map((p, i) => (
                <div key={p.id || p._id || i} className="relative w-20 h-20 rounded-lg overflow-hidden border">
                  <img src={p.preview} alt="" className="w-full h-full object-cover" />
                  <button onClick={() => removePhoto(i)} className="absolute top-0.5 right-0.5 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
              <label className="w-20 h-20 rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors" data-testid="sc-upload-photo">
                <input type="file" accept="image/*" multiple className="hidden" onChange={handlePhotoUpload} />
                {uploading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600" />
                ) : (
                  <>
                    <ImageIcon className="w-5 h-5 text-gray-400" />
                    <span className="text-[10px] text-gray-400 mt-0.5">Ajouter</span>
                  </>
                )}
              </label>
            </div>
          </div>

          {/* Date prévue - Production Calendar */}
          <div>
            <label className="text-sm font-medium mb-1 block">Date prévue — Calendrier de production *</label>
            <MiniProductionCalendar
              selected={form.date_prevue}
              onSelect={d => setForm(f => ({ ...f, date_prevue: d }))}
              existingBons={existingBons}
            />
            {form.date_prevue && (
              <p className="text-xs text-orange-600 mt-1 font-medium">
                Date sélectionnée : {new Date(form.date_prevue + 'T12:00:00').toLocaleDateString('fr-CA', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
              </p>
            )}
          </div>

          {/* Employés */}
          <div>
            <label className="text-sm font-medium mb-1 block">Employés assignés</label>
            <div className="flex flex-wrap gap-1.5">
              {employees.map(emp => {
                const selected = form.employes.find(e => e.id === emp.id);
                return (
                  <button
                    key={emp.id}
                    type="button"
                    onClick={() => toggleEmployee(emp)}
                    className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${selected ? 'bg-blue-100 border-blue-400 text-blue-800' : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'}`}
                    data-testid={`sc-emp-${emp.id}`}
                  >
                    {emp.prenom} {emp.nom}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose}>Annuler</Button>
            <Button onClick={handleSubmit} disabled={saving} className="bg-orange-600 hover:bg-orange-700" data-testid="btn-creer-service-call">
              <Wrench className="w-4 h-4 mr-1" />
              {saving ? 'Création...' : 'Créer le Service Call'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
